import { SITE_URL, getHubForPath } from './sitemap-config'

// Turns "aircon-chemical-wash-and-overhaul" into "Aircon Chemical Wash And Overhaul"
const titleiseSlug = (slug) =>
    decodeURIComponent(slug)
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')

// URLs are emitted without a trailing slash everywhere, so breadcrumb @ids
// always match the canonical / sitemap URLs exactly.
const absolute = (p) => (p === '/' ? `${SITE_URL}/` : SITE_URL + p)

/**
 * Builds a Home -> Hub -> Page trail from a pathname.
 * Derived from the same SERVICE_HUBS list that drives the sitemap, so the two
 * can never disagree.
 *
 * @param {string} pathname  e.g. "/sg/aircon-servicing-singapore/aircon-repair"
 * @param {string} [pageTitle]  overrides the auto-generated leaf label
 * @returns {{name: string, url: string}[]}
 */
export const buildBreadcrumb = (pathname, pageTitle) => {
    const path = (pathname || '/').split('?')[0].replace(/\/$/, '') || '/'
    const trail = [{ name: 'Home', url: absolute('/') }]

    if (path === '/') return trail

    const hub = getHubForPath(path)

    if (hub) {
        trail.push({ name: hub.title, url: absolute(hub.path) })

        if (path !== hub.path) {
            const rest = path.slice(hub.path.length + 1)

            // Intermediate segment for nested groups such as
            // /locality/<name> and /trainer-list/<name>.
            const segments = rest.split('/')
            if (segments.length > 1) {
                const leaf = segments[segments.length - 1]
                trail.push({
                    name: pageTitle || titleiseSlug(leaf),
                    url: absolute(path),
                })
            } else {
                trail.push({
                    name: pageTitle || titleiseSlug(rest),
                    url: absolute(path),
                })
            }
        }
        return trail
    }

    // Non-hub pages: build a trail from the path segments.
    const segments = path.split('/').filter(Boolean)
    let acc = ''
    segments.forEach((segment, i) => {
        acc += `/${segment}`
        const isLast = i === segments.length - 1
        trail.push({
            name: isLast && pageTitle ? pageTitle : titleiseSlug(segment),
            url: absolute(acc),
        })
    })

    return trail
}

/** Schema.org BreadcrumbList for the given trail. */
export const breadcrumbJsonLd = (trail, pageUrl) => ({
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}/#breadcrumblist`,
    itemListElement: trail.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
            '@type': 'WebPage',
            '@id': item.url,
            name: item.name,
            url: item.url,
        },
    })),
})
